import React, { useRef, useState } from "react";
import { SketchPicker } from "react-color";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import {
  ColorInputContainer,
  ColorInputHex,
  ColorInputPickerContainer,
  ColorInputSwatch,
} from "./__styles__/ColorInput";
import Wrapper from "./Wrapper";

const HEX_COLOR_REGEX = /^#[a-fA-F0-9]{6}$/;

interface ColorInputProps {
  name: string;
  value: string;
  onChange: (color: string) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  inline?: boolean;
}

export const ColorInput = ({
  name,
  value,
  onChange,
  label,
  disabled = false,
  required = false,
  inline = false,
}: ColorInputProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter({
    ref: containerRef,
    onOutsideInteraction: () => setShowPicker(false),
  });

  const handleSwatchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setShowPicker(prev => !prev);
    }
  };

  const handleColorChange = (color: { hex: string }) => {
    onChange(color.hex.toUpperCase());
  };

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hex = e.target.value.toUpperCase();
    if (!hex.startsWith("#")) {
      hex = "#" + hex;
    }
    if (/^#[0-9A-F]{0,6}$/.test(hex)) {
      onChange(hex);
    }
  };

  const handleHexBlur = () => {
    if (!HEX_COLOR_REGEX.test(value)) {
      onChange("#000000");
    }
  };

  const content = (
    <ColorInputContainer ref={containerRef} fullWidth={!inline}>
      <ColorInputSwatch
        type="button"
        style={{ backgroundColor: value }}
        onClick={handleSwatchClick}
        disabled={disabled}
      />
      <ColorInputHex
        type="text"
        value={value}
        onChange={handleHexChange}
        onBlur={handleHexBlur}
        disabled={disabled}
        maxLength={7}
        fullWidth={!inline}
      />
      {showPicker && (
        <ColorInputPickerContainer>
          <SketchPicker
            color={value}
            onChange={handleColorChange}
            disableAlpha
          />
        </ColorInputPickerContainer>
      )}
    </ColorInputContainer>
  );

  if (inline) {
    return content;
  }

  return (
    <Wrapper label={label} name={name} required={required}>
      {content}
    </Wrapper>
  );
};

interface ReactHookFormColorInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  inline?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const ReactHookFormColorInput = <T extends FieldValues>({
  control,
  name,
  label,
  inline,
  disabled,
  required,
}: ReactHookFormColorInputProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <ColorInput
        name={name}
        label={label}
        value={field.value}
        onChange={field.onChange}
        inline={inline}
        disabled={disabled}
        required={required}
      />
    )}
  />
);

export default ColorInput;
